import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { cloud, compass, documentText, shieldCheckmark } from 'ionicons/icons';
import { showCookieConsent } from '@/lib/cookie-consent';
import { TermsOfUseContent } from '@/components/TermsOfUseContent';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { PageHeader } from '@/components/PageHeader';

const LAST_UPDATED = 'Last updated: February 13, 2026';

/**
 * Port of disclaimers.page. The footer links here with #termsOfUse and
 * #privacyNotice fragments. The Angular version needed IonContent.scrollToPoint
 * because ion-content owns its own scroll container; on a normal page
 * scrollIntoView works, and scroll-mt on the cards keeps the sticky header
 * from covering the target.
 */
export default function DisclaimersPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const element = document.getElementById(hash.slice(1));
    if (!element) return;
    // One frame so the card is laid out before we measure it.
    const id = requestAnimationFrame(() =>
      element.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
    return () => cancelAnimationFrame(id);
  }, [hash]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <PageHeader
        icon={documentText}
        title="Legal disclaimers"
        subtitle="Important information regarding the use of this website and services"
      />

      <div className="mt-6 flex justify-end print:hidden">
        <Button variant="ghost" onClick={() => window.print()}>
          Print
        </Button>
      </div>

      <div className="mt-2 space-y-6">
        <Card className="scroll-mt-24">
          <CardHeader>
            <CardTitle icon={compass}>Targeting/Advertising Cookies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-slate-700">
            <p>
              <strong className="font-semibold">What Are Cookies?</strong>
              <br />
              Cookies are small text files placed on your device by your browser when you visit a
              website. They allow the website to remember certain information about your visit and
              support secure and efficient operation.
            </p>
            <p>
              <strong className="font-semibold">Types of Cookies We Use</strong>
              <br />
              We do not use cookies for marketing, advertising, or tracking individual users for
              promotional purposes. Our use of cookies is limited to the following:
            </p>
            <ul className="ml-5 list-disc space-y-2">
              <li>
                Strictly Necessary Cookies - Required for the website to operate correctly, such as
                enabling secure login, session management, and navigation.
              </li>
              <li>
                Functional Cookies - Help us remember basic preferences (e.g., display settings)
                and provide an improved user experience.
              </li>
              <li>
                Analytical/Performance Cookies (Limited Use) - May be used in aggregate form to
                understand general website usage (e.g., number of visitors, pages accessed), but
                not to identify or profile individual users. Analytics providers process such
                information solely on our behalf and are contractually restricted from using it for
                their own purposes.
              </li>
            </ul>
            <p>
              <strong className="font-semibold">Cookie and Tracking Disclosure</strong> We use
              cookies and similar technologies on this website to ensure its proper functioning and
              to enhance your browsing experience. We do not sell or share personal information for
              cross-context behavioral advertising.
            </p>
            <p>
              <strong className="font-semibold">No Retail Client Marketing</strong>
              <br />
              This website is intended for institutional and professional users only. We do not
              engage in retail client marketing and therefore do not use targeting or advertising
              cookies.
            </p>
            <p>
              <strong className="font-semibold">Managing Cookies</strong>
              <br />
              You can adjust your browser settings to block or delete cookies if you prefer. Please
              note that some features of the website may not function properly without necessary
              cookies.
            </p>
            <p>
              <strong className="font-semibold">Do Not Track Signals</strong> Industry standards are
              currently evolving, and we may not separately respond to or take action with respect
              to any &ldquo;do not track&rdquo; (&ldquo;DNT&rdquo;) configuration you have enabled
              in your internet browser. At this time, we do not respond to the DNT signal. We do not
              currently recognize or respond to automated browser-based signals such as Global
              Privacy Control unless required by applicable law.
            </p>
            <p className="text-sm text-gray1">{LAST_UPDATED}</p>
            <div className="print:hidden">
              <Button variant="outline" onClick={showCookieConsent}>
                <Icon icon={compass} />
                Manage cookies
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card id="privacyNotice" className="scroll-mt-24">
          <CardHeader>
            <CardTitle icon={shieldCheckmark}>Privacy Notice</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-slate-700">
            <p>
              This website and its services are provided solely for business and institutional use.
              They are not intended for personal, family, or household purposes, and are not
              directed to individual consumers.
            </p>
            <p>
              Our Privacy Policy describes how information is handled in connection with this
              commercial platform and is available in full:{' '}
              <a
                href="/assets/PrivacyStatement20260213.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline hover:text-accent/80"
              >
                Download Privacy Policy (PDF)
              </a>
            </p>
            <p className="text-sm text-gray1">{LAST_UPDATED}</p>
          </CardContent>
        </Card>

        <TermsOfUseContent id="termsOfUse" />

        <Card className="scroll-mt-24">
          <CardHeader>
            <CardTitle icon={cloud}>Service Availability</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-slate-700">
            <p>
              Access to the Site may be interrupted, suspended, or limited from time to time due to
              maintenance, upgrades, system failures, network issues, failures or delays of
              third-party service providers, or other causes. The Site and all functionality are
              provided on an &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE&rdquo; basis, and Tertius
              does not guarantee that the Site will be available at any particular time or free
              from errors, delays, or interruptions.
            </p>
            <p>
              The Site relies on data, systems, and services provided by third parties, including
              administrators, custodians, intermediaries, market data providers, and other vendors.
              Tertius is not responsible for the availability, accuracy, timeliness, or
              completeness of information or services provided by such third parties, or for any
              delays, interruptions, or failures in data transmission or processing caused by them.
            </p>
            <p>
              Tertius may perform scheduled or unscheduled maintenance, updates, or modifications to
              the Site, which may result in temporary unavailability or reduced functionality. To
              the fullest extent permitted by law, Tertius shall not be liable for any
              unavailability, interruption, delay, degradation in performance, or inability to
              access or use Site Data arising from or relating to third-party systems, data
              sources, or services, except as expressly provided in a separate written agreement,
              if any.
            </p>
            <p className="text-sm text-gray1">{LAST_UPDATED}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
